/**
 * CanCooker – Cart Auto-Close
 * ----------------------------
 * 1. Cart Drawer (Dawn)        → closes after AUTO_CLOSE_DELAY ms
 * 2. MW Bundle Builder modal   → closes after AUTO_CLOSE_DELAY ms
 *
 * Timer pauses while the user hovers/focuses inside either element.
 *
 * layout/theme.liquid (before </head>):
 *   <script src="{{ 'cart-drawer-autoclose.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const AUTO_CLOSE_DELAY = 3000; // milliseconds

  // ─── Timer helpers (per-element) ──────────────────────────────────────────

  function startTimer(el, isOpenFn, closeFn) {
    cancelTimer(el);
    el._autoCloseTimer = setTimeout(function () {
      if (isOpenFn(el)) closeFn(el);
    }, AUTO_CLOSE_DELAY);
  }

  function cancelTimer(el) {
    if (el._autoCloseTimer) {
      clearTimeout(el._autoCloseTimer);
      el._autoCloseTimer = null;
    }
  }

  function attachHoverListeners(el, isOpenFn, closeFn) {
    if (el._autoCloseAttached) return;
    el._autoCloseAttached = true;
    el.addEventListener('mouseenter', function () { cancelTimer(el); });
    el.addEventListener('focusin',    function () { cancelTimer(el); });
    el.addEventListener('mouseleave', function () {
      if (isOpenFn(el)) startTimer(el, isOpenFn, closeFn);
    });
    el.addEventListener('focusout', function (e) {
      if (!el.contains(e.relatedTarget) && isOpenFn(el)) {
        startTimer(el, isOpenFn, closeFn);
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 1. DAWN CART DRAWER
  //    open() adds class 'active'; close() removes it.
  // ══════════════════════════════════════════════════════════════════════════

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isDrawerOpen(el) {
    return el ? el.classList.contains('active') : false;
  }

  function closeDrawer(el) {
    if (typeof el.close === 'function') el.close();
  }

  function watchDrawer(drawer) {
    attachHoverListeners(drawer, isDrawerOpen, closeDrawer);
    new MutationObserver(function () {
      if (isDrawerOpen(drawer)) {
        startTimer(drawer, isDrawerOpen, closeDrawer);
      } else {
        cancelTimer(drawer);
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 2. MW BUNDLE BUILDER MODAL
  //    Container : <mw-offers-dialog>
  //    Open state: .Modal-Header has style="transform: translate3d(0px,0px,0px)"
  //    Close btn : the × button inside .Modal-Header
  // ══════════════════════════════════════════════════════════════════════════

  function getMWDialog() {
    return document.querySelector('mw-offers-dialog');
  }

  function isMWModalOpen(dialog) {
    if (!dialog) return false;
    const header = dialog.querySelector('.Modal-Header');
    if (!header) return false;
    // Open = transform translate3d(0,0,0); Closed = translated off-screen
    const t = header.style.transform || '';
    // Match exactly "translate3d(0px, 0px, 0px)" or "translate3d(0px,0px,0px)"
    return /translate3d\(\s*0px,\s*0px,\s*0px\s*\)/.test(t);
  }

  function closeMWModal(dialog) {
    // Click the × close button inside the modal header
    const closeBtn =
      dialog.querySelector('.Modal-Header button') ||
      dialog.querySelector('button[aria-label]') ||
      dialog.querySelector('button');
    if (closeBtn) closeBtn.click();
  }

  function watchMWModal(dialog) {
    attachHoverListeners(dialog, isMWModalOpen, closeMWModal);

    // Watch style changes on .Modal-Header to detect open/close transitions
    const header = dialog.querySelector('.Modal-Header');
    if (!header) return;

    new MutationObserver(function () {
      if (isMWModalOpen(dialog)) {
        startTimer(dialog, isMWModalOpen, closeMWModal);
      } else {
        cancelTimer(dialog);
      }
    }).observe(header, { attributes: true, attributeFilter: ['style'] });

    // Kick off timer if already open when first detected
    if (isMWModalOpen(dialog)) {
      startTimer(dialog, isMWModalOpen, closeMWModal);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // INIT
  // ══════════════════════════════════════════════════════════════════════════

  function init() {
    // Cart drawer — usually already in DOM
    const drawer = getDrawer();
    if (drawer) watchDrawer(drawer);

    // MW dialog may already be in DOM (injected at page load by the app)
    const dialog = getMWDialog();
    if (dialog) {
      watchMWModal(dialog);
    }

    // Watch body for late-injected elements
    new MutationObserver(function () {
      if (!getDrawer()._autoCloseAttached) {
        const d = getDrawer();
        if (d && !d._autoCloseAttached) watchDrawer(d);
      }
      const dlg = getMWDialog();
      if (dlg && !dlg._autoCloseAttached) watchMWModal(dlg);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();